import { useState, useEffect } from 'react';
import { ArrowRight, Phone, MessageCircle, Mic } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Playbook, PlaybookCategory } from '@/types/playbook/playbook';
import { getPlaybookImage } from '../playbook-images';

// Ken Burns animation variants
const kenBurnsVariants = [
  { scale: [1, 1.04], x: [0, -8], y: [0, -5] },
  { scale: [1, 1.05], x: [0, 8], y: [0, -4] },
  { scale: [1, 1.03], x: [0, -5], y: [0, 6] },
  { scale: [1, 1.04], x: [0, 6], y: [0, 4] },
];

interface PlaybookHeroProps {
  playbook: Playbook;
  category: PlaybookCategory;
  onDeploy: () => void;
  onTryDemo: () => void;
  isCreating?: boolean;
}

export const PlaybookHero = ({
  playbook,
  category,
  onDeploy,
  onTryDemo,
  isCreating,
}: PlaybookHeroProps) => {
  const [imageIndex, setImageIndex] = useState(0);

  const heroImage = getPlaybookImage(playbook.meta.id, category.slug);

  useEffect(() => {
    const timer = setInterval(() => {
      setImageIndex((prev) => (prev + 1) % kenBurnsVariants.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <section className="relative w-full h-auto sm:h-[78vh] md:h-[84vh] min-h-0 sm:min-h-[600px] md:min-h-[660px] flex items-center text-white overflow-hidden">
        {/* Background Image with Ken Burns */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <AnimatePresence mode="sync">
            <motion.img
              key={imageIndex}
              src={heroImage}
              alt={`${playbook.meta.name} hero background`}
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ opacity: 0, scale: 1 }}
              animate={{
                opacity: 1,
                scale: kenBurnsVariants[imageIndex].scale,
                x: kenBurnsVariants[imageIndex].x,
                y: kenBurnsVariants[imageIndex].y,
              }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 1.2, ease: 'easeInOut' },
                scale: { duration: 8, ease: 'linear' },
                x: { duration: 8, ease: 'linear' },
                y: { duration: 8, ease: 'linear' },
              }}
              onError={(e) => {
                e.currentTarget.src = '/hero-background.webp';
              }}
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-black/70 z-10" />
        </div>

        {/* Main content */}
        <div className="relative z-10 w-full flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-5xl w-full pt-20 pb-10 sm:py-0">
            {/* Category + Channel badges — hidden on mobile */}
            <div className="hidden sm:flex mb-5 flex-row items-center justify-center gap-3">
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[#4E9CFF] to-[#28406F] rounded-full">
                <span className="text-base font-semibold text-white">
                  {category.name}
                </span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full border border-white/20">
                <Mic className="h-3.5 w-3.5 text-white" />
                <span className="text-xs font-medium text-white/90">Voice</span>
                <span className="text-white/40 text-xs">+</span>
                <MessageCircle className="h-3.5 w-3.5 text-white" />
                <span className="text-xs font-medium text-white/90">Text</span>
              </div>
            </div>

            {/* Playbook name */}
            <h1 className="font-bold leading-tight drop-shadow-2xl mb-4 text-4xl sm:text-5xl md:text-6xl lg:text-7xl break-words">
              <span className="text-white">{playbook.meta.name}</span>
            </h1>

            {/* Tagline with gradient */}
            <p className="font-semibold bg-gradient-to-r from-blue-300 via-purple-300 to-pink-300 bg-clip-text text-transparent drop-shadow-lg max-w-3xl mx-auto mb-4 text-lg sm:text-xl md:text-2xl lg:text-[1.75rem]">
              {playbook.meta.tagline}
            </p>

            {/* Description */}
            <p className="text-white/80 font-normal drop-shadow-lg max-w-3xl mx-auto text-sm sm:text-base md:text-lg lg:text-xl">
              {playbook.meta.description}
            </p>

            {/* Channel + Stats Row */}
            <div className="mt-5 flex flex-wrap gap-3 justify-center items-center">
              <div className="flex items-center gap-2 text-gray-200 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
                <Mic className="h-3.5 w-3.5" />
                <span className="text-xs">Phone & Voice</span>
              </div>
              <div className="flex items-center gap-2 text-gray-200 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
                <MessageCircle className="h-3.5 w-3.5" />
                <span className="text-xs">Web Chat & SMS</span>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button
                size="lg"
                onClick={onDeploy}
                disabled={isCreating}
                className="rounded-lg bg-gradient-to-r from-[#4E9CFF] to-[#28406F] text-white font-semibold shadow-lg hover:from-[#3d8de6] hover:to-[#1f3459] transition-all hover:-translate-y-0.5 touch-manipulation min-h-[48px] px-6 sm:px-8 text-base sm:text-lg"
              >
                {isCreating ? 'Deploying...' : 'Deploy This Playbook'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <Button
                size="lg"
                variant="outline"
                onClick={onTryDemo}
                className="rounded-lg bg-white/10 text-white border-2 border-white/50 hover:bg-white/20 hover:border-white font-semibold shadow-lg backdrop-blur-sm transition-all hover:-translate-y-0.5 touch-manipulation min-h-[48px] px-6 sm:px-8 text-base sm:text-lg"
              >
                <Phone className="mr-2 h-5 w-5" />
                Talk To Me
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Shimmer animation styles */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};
